import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, TrendingUp, Award, Target, BarChart3, X } from 'lucide-react';
import type { FinalResults } from '../types';
import Logo from '../assests/favicon-logo.png'

interface CompetitorAnalysisProps {
  results: FinalResults;
  isOpen: boolean;
  onClose: () => void;
}

const CompetitorAnalysis: React.FC<CompetitorAnalysisProps> = ({
  results,
  isOpen,
  onClose
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const score = results.globalScore;
  
  // Simulation de données de marché basées sur le score
  const getMarketData = () => {
    const baseData = {
      totalCandidates: Math.floor(Math.random() * 200) + 150,
      averageScore: Math.floor(Math.random() * 20) + 65,
      topPercentile: Math.floor(Math.random() * 15) + 85,
      yourRanking: 0,
      competitionLevel: 'medium'
    };

    // Calculer le ranking basé sur le score
    if (score >= baseData.topPercentile) {
      baseData.yourRanking = Math.floor(Math.random() * 10) + 1;
      baseData.competitionLevel = 'low';
    } else if (score >= baseData.averageScore) {
      baseData.yourRanking = Math.floor(Math.random() * 30) + 11;
      baseData.competitionLevel = 'medium';
    } else {
      baseData.yourRanking = Math.floor(Math.random() * 50) + 41;
      baseData.competitionLevel = 'high';
    }

    return baseData;
  };

  const marketData = getMarketData();

  const getCompetitionColor = (level: string) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-orange-600 bg-orange-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCompetitionText = (level: string) => {
    switch (level) {
      case 'low': return t('competitor.competition.low');
      case 'medium': return t('competitor.competition.medium');
      case 'high': return t('competitor.competition.high');
      default: return t('competitor.competition.unknown');
    }
  };

  const skillsGaps = [
    { skill: 'React.js', yourLevel: t('competitor.skills.react.level'), marketDemand: t('competitor.skills.react.demand'), gap: t('competitor.skills.react.gap') },
    { skill: 'TypeScript', yourLevel: t('competitor.skills.ts.level'), marketDemand: t('competitor.skills.ts.demand'), gap: t('competitor.skills.ts.gap') },
    { skill: 'AWS', yourLevel: t('competitor.skills.aws.level'), marketDemand: t('competitor.skills.aws.demand'), gap: t('competitor.skills.aws.gap') },
    { skill: 'Docker', yourLevel: t('competitor.skills.docker.level'), marketDemand: t('competitor.skills.docker.demand'), gap: t('competitor.skills.docker.gap') }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between relative">
          <div className="flex items-center">
            <BarChart3 className="w-6 h-6 text-blue-600 mr-3" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">📊 {t('competitor.title')}</h2>
              <p className="text-gray-600 mt-1">{t('competitor.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors absolute sm:relative top-[5px] right-[5px] sm:top-[unset] sm:right-[unset]"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Vue d'ensemble du marché */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
              <div className="flex items-center justify-between mb-4">
                <Users className="w-8 h-8 text-blue-600" />
                <span className="text-2xl font-bold text-blue-800">{marketData.totalCandidates}</span>
              </div>
              <h3 className="font-semibold text-blue-900 mb-1">{t('competitor.similar_candidates')}</h3>
              <p className="text-sm text-blue-700">{t('competitor.for_this_role')}</p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
                <span className="text-2xl font-bold text-green-800">#{marketData.yourRanking}</span>
              </div>
              <h3 className="font-semibold text-green-900 mb-1">{t('competitor.your_ranking')}</h3>
              <p className="text-sm text-green-700">{t('competitor.out_of_candidates', { count: marketData.totalCandidates })}</p>
            </div>

            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200">
              <div className="flex items-center justify-between mb-4">
                <Award className="w-8 h-8 text-purple-600" />
                <span className="text-2xl font-bold text-purple-800">{score}%</span>
              </div>
              <h3 className="font-semibold text-purple-900 mb-1">{t('competitor.your_score')}</h3>
              <p className="text-sm text-purple-700">{t('competitor.vs_average', { avg: marketData.averageScore })}</p>
            </div>
          </div>

          {/* Niveau de concurrence */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Target className="w-5 h-5 mr-2 text-orange-500" />
              {t('competitor.competition_level')}
            </h3>
            
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-700">{t('competitor.competition_for_role')}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCompetitionColor(marketData.competitionLevel)}`}>
                {getCompetitionText(marketData.competitionLevel)}
              </span>
            </div>

            <div className="bg-gray-100 rounded-full h-4 mb-2">
              <div 
                className="bg-gradient-to-r from-green-500 to-blue-500 h-4 rounded-full transition-all duration-1000"
                style={{ width: `${(score / 100) * 100}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between text-xs text-gray-600">
              <span>{t('competitor.low_competition')}</span>
              <span>{t('competitor.high_competition')}</span>
            </div>
          </div>

          {/* Analyse des compétences vs marché */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 mb-8">
            <div className="flex gap-x-2">
              <img src={Logo} className='max-h-7 max-w-7 min-h-7 min-w-7 rounded-full'/>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {t('competitor.skills_vs_market')}
              </h3>
            </div>
            
            <div className="space-y-4">
              {skillsGaps.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{item.skill}</h4>
                    <div className="flex items-center space-x-4 mt-1 text-sm">
                      <span className="text-gray-600">{t('competitor.your_level')}: <strong>{item.yourLevel}</strong></span>
                      <span className="text-gray-600">{t('competitor.market_demand')}: <strong>{item.marketDemand}</strong></span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      item.gap === t('competitor.skills.gap.none') ? 'bg-green-100 text-green-800' :
                      item.gap === t('competitor.skills.gap.minor') ? 'bg-yellow-100 text-yellow-800' :
                      item.gap === t('competitor.skills.gap.significant') ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.gap}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recommandations stratégiques */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
            <h3 className="text-lg font-semibold text-indigo-900 mb-4">
              🚀 {t('competitor.strategic_recommendations')}
            </h3>
            
            <div className="space-y-3">
              {marketData.competitionLevel === 'high' && (
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3"></div>
                  <p className="text-indigo-800 text-sm">
                    <strong>{t('competitor.priority_high')}</strong> {t('competitor.recommendation_aws_docker')}
                  </p>
                </div>
              )}
              
              <div className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 mr-3"></div>
                <p className="text-indigo-800 text-sm">
                  <strong>{t('competitor.optimization_cv')}</strong> {t('competitor.recommendation_react')}
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3"></div>
                <p className="text-indigo-800 text-sm">
                  <strong>{t('competitor.positioning')}</strong> {t('competitor.recommendation_positioning', { percent: Math.round((marketData.yourRanking / marketData.totalCandidates) * 100) })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompetitorAnalysis;